import React, { useEffect, useState } from "react";
import HeroSection from "./herosection";
import { Col, Row, Button, Switch } from "antd";
import { CheckCircleOutlined } from "@ant-design/icons";

function Pricing() {
  const [timeFrame, setTimeFrame] = useState("monthly");
  const [screenSize, setScreenSize] = useState("desktop");

  useEffect(() => {
    document.title = "Pricing - Optix360";

    const handleResize = () => {
      if (window.innerWidth <= 768) {
        setScreenSize("mobile");
      } else if (window.innerWidth <= 1024) {
        setScreenSize("tablet");
      } else {
        setScreenSize("desktop");
      }
    };

    // Set initial screen size
    handleResize();

    // Add event listener
    window.addEventListener("resize", handleResize);

    // Clean up event listener
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const getPaddingStyle = () => {
    switch (screenSize) {
      case "mobile":
        return {
          textAlign: "center",
          padding: "30px 15px",
          color: "#000",
          backgroundColor: "#fff",
        };
      case "tablet":
        return {
          textAlign: "center",
          padding: "40px 30px",
          color: "#000",
          backgroundColor: "#fff",
        };
      default:
        return {
          textAlign: "center",
          padding: "50px 110px 80px 110px",
          color: "#000",
          backgroundColor: "#fff",
        };
    }
  };

  const getColumnSpan = () => {
    switch (screenSize) {
      case "mobile":
        return 24;
      case "tablet":
        return 12;
      default:
        return 7;
    }
  };

  const getTitleStyle = () => {
    switch (screenSize) {
      case "mobile":
        return { textAlign: "center", fontSize: "24px", fontWeight: "bold", letterSpacing: "1px", lineHeight: "30px" };
      case "tablet":
        return { textAlign: "center", fontSize: "30px", fontWeight: "bold", letterSpacing: "1px", lineHeight: "38px" };
      default:
        return { textAlign: "center", fontSize: "35px", fontWeight: "bold", letterSpacing: "1px", lineHeight: "45px" };
    }
  };

  const getSubtitleStyle = () => {
    switch (screenSize) {
      case "mobile":
        return { textAlign: "center", fontSize: "14px", fontWeight: "normal", letterSpacing: "0.5px", lineHeight: "20px", color: "rgb(75, 85, 99)", margin: "10px 0 20px 0" };
      case "tablet":
        return { textAlign: "center", fontSize: "15px", fontWeight: "normal", letterSpacing: "0.8px", lineHeight: "22px", color: "rgb(75, 85, 99)", margin: "15px 0 25px 0" };
      default:
        return { textAlign: "center", fontSize: "16px", fontWeight: "normal", letterSpacing: "1px", lineHeight: "25px", color: "rgb(75, 85, 99)", margin: "20px 0 30px 0" };
    }
  };

  const getCardStyle = () => {
    return {
      display: "flex",
      flexDirection: "column",
      height: "100%",
      minHeight: screenSize === "mobile" ? "500px" : "550px",
      borderRadius: "16px",
      boxShadow: "0 4px 12px rgba(0,0,0,0.25)",
      overflow: "hidden",
      backgroundColor: "#fff",
      textAlign: "left",
      lineHeight: "1.5",
    };
  };

  const monthlyPlans = [
    {
      title: "Free",
      subtitle: "for 3 Months",
      price: null,
      features: [
        "Customer Management",
        "Sales Tracking",
        "Inventory Management",
        "Members Management",
        "Billing",
        "Wholesale",
      ],
      button: "Get Started",
    },
    {
      title: "Retailer",
      subtitle: "1,200 PKR/month",
      price: 1200,
      features: [
        "Customer Management",
        "Sales Tracking",
        "Inventory Management",
        "Limited Members",
        "Limited E-Store (Coming Soon)",
        "Billing",
        "Add Upto 1 Store",
        "Wholesale",
      ],
      button: "Get Started",
    },
    {
      title: "Wholesale & Retailer",
      subtitle: "2,200 PKR/month",
      price: 2200,
      features: [
        "Customer Management",
        "Sales Tracking",
        "Inventory Management",
        "Members Management",
        "E-Store (Coming Soon)",
        "Billing",
        "Add Multiple Stores",
        "Wholesale",
      ],
      button: "Get Started",
    },
  ];

  const yearlyPlans = [
    {
      title: "Free",
      subtitle: "for 3 Months",
      price: null,
      features: [
        "Customer Management",
        "Sales Tracking",
        "Inventory Management",
        "Members Management",
        "Billing",
        "Wholesale",
      ],
      button: "Get Started",
    },
    {
      title: "Retailer",
      subtitle: "13,000 PKR/year",
      price: 13000,
      features: [
        "Customer Management",
        "Sales Tracking",
        "Inventory Management",
        "Limited Members",
        "Limited E-Store (Coming Soon)",
        "Billing",
        "Add Upto 1 Store",
        "Wholesale",
      ],
      button: "Get Started",
    },
    {
      title: "Wholesale & Retailer",
      subtitle: "25,000 PKR/year",
      price: 25000,
      features: [
        "Customer Management",
        "Sales Tracking",
        "Inventory Management",
        "Members Management",
        "E-Store (Coming Soon)",
        "Billing",
        "Add Multiple Stores",
        "Wholesale",
      ],
      button: "Get Started",
    },
  ];

  const plans = timeFrame === "monthly" ? monthlyPlans : yearlyPlans;

  return (
    <>
      <HeroSection />
      <div style={getPaddingStyle()}>
        <Col
          span={screenSize === "mobile" ? 24 : 14}
          offset={screenSize === "mobile" ? 0 : 5}
          style={{ marginBottom: "40px" }}
        >
          <h1 style={getTitleStyle()}>Choose a Plan</h1>
          <p style={getSubtitleStyle()}>
            Take control of your operations with flexible plans tailored to your business needs.
            Optix360 brings together sales, inventory, customers and members management in one seamless platform—
            built to simplify your workflow and scale with you. Whether you're a solo shop owner and managing multiple employees,
            our pricing options are designed to grow alongside your business. Explore our plans and find the perfect fit today.
          </p>
        </Col>
        <div style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          marginBottom: "30px",
          gap: "12px",
          flexDirection: screenSize === "mobile" ? "column" : "row"
        }}>
          <span style={{
            fontWeight: timeFrame === "monthly" ? "bold" : "normal",
            marginBottom: screenSize === "mobile" ? "10px" : "0"
          }}>
            Monthly
          </span>
          <Switch
            checked={timeFrame === "yearly"}
            onChange={checked => setTimeFrame(checked ? "yearly" : "monthly")}
            checkedChildren="Yearly"
            unCheckedChildren="Monthly"
          />
          <span style={{
            fontWeight: timeFrame === "yearly" ? "bold" : "normal",
            marginTop: screenSize === "mobile" ? "10px" : "0"
          }}>
            Yearly
          </span>
        </div>

        <Row
          gutter={[16, 24]}
          justify="center"
          style={{
            marginTop: "20px",
            padding: screenSize === "mobile" ? "0 10px" : "0 20px"
          }}
        >
          {plans.map((plan, idx) => (
            <Col
              span={getColumnSpan()}
              key={plan.title}
              style={{ height: "100%" }}
            >
              <div style={getCardStyle()}>
                {/* Card Body */}
                <div style={{ padding: "24px", flexGrow: 1 }}>
                  <h1 style={{
                    color: "#001529",
                    marginBottom: "8px",
                    fontSize: screenSize === "mobile" ? "20px" : "24px"
                  }}>
                    {plan.title}
                  </h1>

                  <p
                    style={{
                      fontSize: "12px",
                      fontWeight: "normal",
                      letterSpacing: "1px",
                      color: "rgb(75, 85, 99)",
                      marginTop: "-8px",
                    }}
                  >
                    {plan.subtitle}
                  </p>

                  <p
                    style={{
                      fontSize: screenSize === "mobile" ? "16px" : "18px",
                      fontWeight: "500",
                      lineHeight: "25px",
                      color: "rgb(75, 85, 99)",
                      marginTop: "20px",
                    }}
                  >
                    Features include:
                  </p>

                  <ul
                    style={{
                      fontSize: screenSize === "mobile" ? "14px" : "16px",
                      fontWeight: "normal",
                      letterSpacing: "1px",
                      lineHeight: "25px",
                      color: "rgb(75, 85, 99)",
                      marginTop: "10px",
                      listStyle: "none",
                      paddingLeft: 0,
                      display: "flex",
                      flexDirection: "column",
                      gap: "10px",
                      minHeight: "160px",
                    }}
                  >
                    {plan.features.map((feature, fidx) =>
                      feature?.trim() ? (
                        <li key={fidx}>
                          <CheckCircleOutlined style={{ marginRight: "8px" }} />
                          {feature}
                        </li>
                      ) : null
                    )}
                  </ul>
                </div>

                {/* Button Footer */}
                <div style={{ padding: "16px", borderTop: "1px solid #f0f0f0" }}>
                  <Button
                    type="primary"
                    style={{
                      width: "100%",
                      height: "40px",
                      borderRadius: "8px",
                    }}
                  >
                    {plan.button}
                  </Button>
                </div>
              </div>
            </Col>
          ))}
        </Row>
      </div>
    </>
  );
}

export default Pricing;